import { useEffect, useState, useMemo } from "react"

import { useBookingsContext } from "../hooks/useBookingsContext"
import { useAuthContext } from "../hooks/useAuthContext"

import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar'

import format from "date-fns/format"
import parse from "date-fns/parse"
import startOfWeek from "date-fns/startOfWeek"
import getDay from "date-fns/getDay"
import enUS from "date-fns/locale/sv"

import 'react-big-calendar/lib/css/react-big-calendar.css'
import '../styling/calendar.css'
import { strings } from '../strings'

import CalendarToolbar from '../components/CalendarToolbar'

import {
    daysAheadLaundry,
    laundryDuration,
    firstAvailableLaundryStartTime,
    lastAvailableLaundryEndTime,
    maximumNumberOfLaundryBookings
} from '../config'
import ConfirmationDialog from "./dialogs/ConfirmationDialog"
import LimitDialog from "./dialogs/LimitDialog"
import EditEventDialog from "./dialogs/EditEventDialog"

const LaundryCalendar = ({ facilityID }) => {

    const { bookings, bookedLaundrySlots, dispatch } = useBookingsContext()
    const [error, setError] = useState(null)
    const [bookingSlots, setBookingSlots] = useState([])
    const { user } = useAuthContext()
    const [open, setOpen] = useState(false)
    const [limitOpen, setLimitOpen] = useState(false)
    const [selected, setSelected] = useState(null)
    const [editOpen, setEditOpen] = useState(false)
    const [limit, setLimit] = useState("")

    const openConfirmationDialog = () => {
        setOpen(true)
    }

    const confirmBooking = async (machine1, machine2) => {
        setOpen(false)
        setError(null)

        const requestedMachines = (machine1 && !machine2) ? 1 : (!machine1 && machine2) ? 2 : 3
        const selectedWithMachines = { ...selected, machineID: requestedMachines, userName: user.userID }

        const response = await fetch(process.env.REACT_APP_SERVER_URL + '/api/bookings/', {
            method: 'POST',
            body: JSON.stringify(selectedWithMachines),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`,
            }
        })

        const json = await response.json()

        if (!response.ok) {
            setError(json.msg)
        }

        if (response.ok) {
            setError(null)
            console.log("New booking: ", json)
            dispatch({ type: 'CREATE_BOOKING', payload: json })
        }
    }

    const cancelBooking = () => {
        setOpen(false)
    }

    const openEditDialog = () => {
        setEditOpen(true)
    }

    const cancelEdit = () => {
        setEditOpen(false)
    }

    const openLimitDialog = () => {
        setLimitOpen(true)
    }

    const closeLimitDialog = () => {
        setLimitOpen(false)
    }

    const { components } = useMemo(
        () => ({
            components: {
                toolbar: props => (<CalendarToolbar {...props} />)
            }
        }),
        []
    )

    const eventPropGetter = (event) => {
        if (bookedLaundrySlots) {

            let assignedBookings = bookedLaundrySlots.filter(obj => { return obj.start === event.start.toISOString() })
            const RED = '#E60B0055'
            const ORANGE = '#FA6C0055'
            const GREEN = '#00660055'
            const BLUE = '#6684F0'

            if (assignedBookings.length === 0) {
                return { style: { backgroundColor: GREEN } }
            }

            if (assignedBookings.length === 1) {
                if (assignedBookings[0].userName === user.userID) {
                    return { style: { backgroundColor: BLUE } }
                } else {
                    if (assignedBookings[0].machineID === 3) {
                        return { style: { backgroundColor: RED } }
                    } else {
                        return { style: { backgroundColor: ORANGE } }
                    }

                }
            }

            if (assignedBookings.length === 2) {
                if (assignedBookings[0].userName === user.userID || assignedBookings[1].userName === user.userID) {
                    return { style: { backgroundColor: BLUE } }
                } else {
                    return { style: { backgroundColor: RED } }
                }
            }
        }
    }


    useEffect(() => {
        const fetchBookings = async () => {
            setError(null)

            const response = await fetch(process.env.REACT_APP_SERVER_URL + '/api/bookings/laundry', {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                    'facilityID': facilityID
                }
            })
            const json = await response.json()

            if (!response.ok) {
                setError(json.msg)
            }

            if (response.ok) {
                setError(null)

                const relevantBookings = json.filter((b) => {
                    return new Date(b.start) > new Date()
                })
                dispatch({ type: 'SET_BOOKED_LAUNDRY_SLOTS', payload: relevantBookings })
            }
        }

        if (user) {
            fetchBookings()

            const intervalId = setInterval(() => {
                fetchBookings()
            }, 30000) //ms

            return () => clearInterval(intervalId)

        }


    }, [dispatch, user, facilityID])

    const today = new Date()
    const locales = {
        "sv-SE": enUS,
    }

    const localizer = dateFnsLocalizer({
        format,
        parse,
        startOfWeek: () => { return startOfWeek(new Date(), { weekStartsOn: 1 }) },
        getDay,
        locales,
    })

    useEffect(() => {

        const addMinutes = (date, minutes) => {
            const newDate = new Date(date)
            newDate.setMinutes(newDate.getMinutes() + minutes)
            return newDate
        }

        const populateSchedule = () => {
            const eventsList = [];
            const slotDuration = 60 * laundryDuration

            const now = new Date()
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

            for (let day = 0; day < daysAheadLaundry; day++) {
                const dayStart = new Date(today)
                dayStart.setDate(dayStart.getDate() + day)
                dayStart.setHours(firstAvailableLaundryStartTime, 0, 0, 0)

                let slotStart = new Date(dayStart)

                while (true) {
                    const slotEnd = addMinutes(slotStart, slotDuration)
                    const endHour = slotEnd.getHours() + slotEnd.getMinutes() / 60

                    if (endHour > lastAvailableLaundryEndTime) {
                        break;
                    }

                    if (slotStart > now) {
                        eventsList.push({
                            id: eventsList.length + 1,
                            start: new Date(slotStart),
                            end: new Date(slotEnd),
                        })
                    }

                    slotStart = addMinutes(slotStart, slotDuration)
                }
            }

            setBookingSlots(eventsList)
        }

        populateSchedule()

    }, [])

    let w = window.innerWidth
    let h = window.innerHeight

    const webEventTimeFormat = ({ start, end }, culture, localizer) =>
        localizer.format(start, 'kk:mm', 'sv-SE') +
        ' - ' +
        localizer.format(end, 'kk:mm', 'sv-SE')

    const mobileEventTimeFormat = ({ start, end }, culture, localizer) =>
        localizer.format(start, 'kk:mm', 'sv-SE')

    const formats = {
        dayFormat: (date, culture, localizer) => localizer.format(date, 'eee d', 'sv-SE'),
        dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
            localizer.format(start, 'd MMM', 'sv-SE')
            + ' - ' +
            localizer.format(end, 'd MMM', 'sv-SE'),
        eventTimeRangeFormat: h < w ? webEventTimeFormat : mobileEventTimeFormat
    }

    const handleClick = (e) => {

        const facility = strings.laundry.laundryRoom + ' ' + facilityID
        const start = e.start
        const duration = laundryDuration
        let clickedBooking = { facility, facilityID, start, duration }
        let hours = Math.abs(start - new Date()) / 36e5

        const myOngoing = bookings.filter((e) => { return e.facility !== strings.balcony.balcony }) // My ongoing bookings
        const bookingsInSlot = bookedLaundrySlots.filter(obj => { return obj.start === e.start.toISOString() }) // Assigned bookings to this slot
        let bookingMadeByMe = false

        if (bookingsInSlot.length === 0) { // Free to book unless already two ongoing (GREEN)
            bookEvent(myOngoing, { ...clickedBooking, userName: user.userID }) // Add my name to booking
        }

        if (bookingsInSlot.length === 1) { // Slot has one booking (RED, ORANGE OR BLUE)

            bookingMadeByMe = bookingsInSlot[0].userName === user.userID
            if (bookingMadeByMe) { // BLUE
                if (hours <= 1) {
                    setLimit(strings.dialogs.tooLate) // Too late to edit
                    openLimitDialog()
                    return
                }
                setSelected({ ...clickedBooking, _id: bookingsInSlot[0]._id, machineID: bookingsInSlot[0].machineID })
                openEditDialog()
            } else { // Booking made by someone else
                if (bookingsInSlot[0].machineID === 3) { // Fully booked
                    if (myOngoing.length >= maximumNumberOfLaundryBookings) {
                        setLimit(strings.dialogs.limit)
                    } else {
                        setLimit(strings.dialogs.fullyBooked + " av " + bookingsInSlot[0].userName)
                    }
                    openLimitDialog()
                } else { // One booked by someone else
                    bookEvent(myOngoing, { ...clickedBooking, userName: bookingsInSlot[0].userName, machineID: bookingsInSlot[0].machineID })
                }

            }

        }

        if (bookingsInSlot.length === 2) { // Slot has two bookings (RED OR BLUE)
            let myBookings = bookingsInSlot.filter((b) => { return b.userName === user.userID })

            if (myBookings.length === 0) { // None of the bookings are mine

                let bookers = bookingsInSlot[0].userName !== bookingsInSlot[1].userName ? bookingsInSlot[0].userName + " och " + bookingsInSlot[1].userName : bookingsInSlot[0].userName

                if (myOngoing.length >= maximumNumberOfLaundryBookings) {
                    setLimit(strings.dialogs.limit)
                } else {
                    setLimit(strings.dialogs.fullyBooked + " av " + bookers)
                }
                openLimitDialog()
            }

            if (myBookings.length === 1) {
                if (hours <= 1) {
                    setLimit(strings.dialogs.tooLate)
                    openLimitDialog()
                    return
                }
                setSelected({ ...clickedBooking, _id: myBookings[0]._id, machineID: myBookings[0].machineID, takenBySomeoneElse: true })
                openEditDialog()
            }
        }

    }

    const bookEvent = (myOngoing, clickedBooking) => {
        if (myOngoing.length >= maximumNumberOfLaundryBookings) {
            setLimit(strings.dialogs.limit)
            openLimitDialog()
        } else {
            setSelected(clickedBooking)
            openConfirmationDialog()
        }
    }


    return (
        <div className="calendar-container">
            <ConfirmationDialog open={open} event={selected} closeConfirm={confirmBooking} closeCancel={cancelBooking} />
            <LimitDialog open={limitOpen} closeDialog={closeLimitDialog} limit={limit} title={strings.dialogs.limitTitle} />
            <EditEventDialog open={editOpen} closeCancel={cancelEdit} event={selected} />

            <Calendar
                defaultView={Views.WEEK}
                events={bookingSlots}
                localizer={localizer}
                step={10}
                timeslots={1}
                formats={formats}
                components={components}
                onSelectEvent={handleClick}
                min={
                    new Date(
                        today.getFullYear(),
                        today.getMonth(),
                        today.getDate(),
                        firstAvailableLaundryStartTime
                    )
                }
                max={
                    new Date(
                        today.getFullYear(),
                        today.getMonth(),
                        today.getDate(),
                        lastAvailableLaundryEndTime, 10
                    )}
                eventPropGetter={eventPropGetter}
            />
            {error && <div className="error">{error}</div>}

        </div>
    )
}

export default LaundryCalendar
